import { ref, computed, nextTick } from "vue";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { debounce } from "@/utils/debounce";
import { formatDocumentCardDescription } from "@/views/encounter-notes/utils/actionCardUtils";
import { isUSCustomer, getGeneticsTestIds } from "@/store/modules/va-order-test/helpers/tests";
import { getCachedOrderPageData, preloadOrderPageData } from "@/utils/va-order-test/preloadService";

import taskIcon from "@/assets/encounterNotes/alarm.svg";
import appointmentIcon from "@/assets/encounterNotes/event.svg";
import orderIcon from "@/assets/MyPatient/Vector.svg";
import documentIcon from "@/assets/encounterNotes/docs.svg";

// ============================================================================
// Shared menu helpers
// ============================================================================

/**
 * Get caret coordinates relative to the viewport for fixed-positioned menus.
 * Returns { top, bottom, left, showAbove } or null.
 */
const getCaretCoordinates = (editorInstance) => {
  if (!editorInstance) return null;
  const { view } = editorInstance;
  const { from } = view.state.selection;
  const coords = view.coordsAtPos(from);
  const browserHeight = document.documentElement.clientHeight;
  const isNearBottom = coords.bottom > browserHeight * 0.7;

  if (isNearBottom) {
    return { bottom: browserHeight - coords.top + 4, left: coords.left, showAbove: true };
  }
  return { top: coords.bottom + 4, left: coords.left, showAbove: false };
};

/**
 * Convert caret coordinates to CSS position object for a menu.
 */
const coordsToPosition = (coords) => {
  if (!coords) return {};
  if (coords.showAbove) {
    return { bottom: `${coords.bottom}px`, top: 'auto', left: `${coords.left}px` };
  }
  return { top: `${coords.top}px`, bottom: 'auto', left: `${coords.left}px` };
};

/**
 * Generic arrow/enter/escape key handler for any dropdown menu.
 * @param {Event} event
 * @param {import('vue').Ref<Array>} itemsRef - The filtered/visible items
 * @param {import('vue').Ref<number>} selectedIndexRef
 * @param {Function} executeItem - Called with the selected item on Enter
 * @param {Function} closeMenu - Called on Escape
 * @param {Object} [options]
 * @param {boolean} [options.dismissOnOtherKey] - If true, close menu on any non-nav key
 * @returns {boolean} Whether the event was handled
 */
const handleMenuKeydown = (event, itemsRef, selectedIndexRef, executeItem, closeMenu, options = {}) => {
  const items = itemsRef.value || itemsRef;
  const len = Array.isArray(items) ? items.length : 0;
  if (len === 0) return false;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      selectedIndexRef.value = (selectedIndexRef.value + 1) % len;
      return true;
    case 'ArrowUp':
      event.preventDefault();
      selectedIndexRef.value = (selectedIndexRef.value - 1 + len) % len;
      return true;
    case 'Enter':
      event.preventDefault();
      if (items[selectedIndexRef.value]) {
        executeItem(items[selectedIndexRef.value]);
      }
      return true;
    case 'Escape':
      event.preventDefault();
      closeMenu();
      return true;
    default:
      if (options.dismissOnOtherKey) {
        closeMenu();
      }
      return false;
  }
};

// ============================================================================
// Composable
// ============================================================================

/**
 * @param {Object} options
 * @param {import('vue').Ref} options.editor - TipTap editor ref
 * @param {Object} options.props - Component props (isDraft, patientVariables, patientMatchData)
 * @param {Function} options.emit - Component emit function
 * @param {Function} options.scheduleSave - Trigger a debounced save
 * @param {Function} options.insertSlashCommandResult - Insert content returned by slash command
 */
export function useEditorMenus({ editor, props, emit, scheduleSave, insertSlashCommandResult }) {

  // ========================================================================
  // Disabled test IDs (mirrors filtering logic from setAllTests in store)
  // ========================================================================
  const vwTestCategory = ref([]);

  // Load vwTestCategory from cache; if empty, fetch via API and cache it
  (async () => {
    let currencyName = 'usd';
    try { currencyName = JSON.parse(localStorage.getItem('currency'))?.name?.toLowerCase() || 'usd'; } catch (e) { /* ignore */ }

    let cachedData = getCachedOrderPageData(currencyName);
    if (!cachedData) {
      await preloadOrderPageData(currencyName);
      cachedData = getCachedOrderPageData(currencyName);
    }
    vwTestCategory.value = cachedData?.vwTestCategory || [];
  })();

  const disabledTestIds = computed(() => {
    const disabled = new Set();
    let userInfo = null;
    try { userInfo = JSON.parse(sessionStorage.getItem('userInfo')); } catch (e) { /* ignore */ }
    if (!userInfo) return disabled;

    const { user_roles = [], old_clinic_id, beta_programs = [] } = userInfo;

    // Rupa (clinic 1007150) or agent role: disable Regenere (731) & NutriProZ (725, 726)
    if (old_clinic_id === 1007150 || user_roles.includes('agent')) {
      [731, 725, 726].forEach(id => disabled.add(id));
    }

    // Tickborne Antibodies (832): disable if not in tickbone_customer beta
    if (!beta_programs.includes('tickbone_customer')) {
      disabled.add(832);
    }

    // Gut Zoomer - Stool Only (836): disable if not in gz4_whitelist beta
    if (!beta_programs.includes('gz4_whitelist')) {
      disabled.add(836);
    }

    // Hormone Zoomer - Urine Only (835): disable if not in hz_urine_whitelist beta
    if (!beta_programs.includes('hz_urine_whitelist')) {
      disabled.add(835);
    }

    // Nutrient Zoomer Microtube (850): disable if not in nzm_whitelist beta
    if (!beta_programs.includes('nzm_whitelist')) {
      disabled.add(850);
    }

    // Genetics: disable for non-US users without genetics_whitelist beta
    const hasGeneticsWhitelist = Array.isArray(beta_programs) && beta_programs.includes('genetics_whitelist');
    if (!isUSCustomer(userInfo) && !hasGeneticsWhitelist) {
      getGeneticsTestIds(vwTestCategory.value).forEach(id => disabled.add(id));
    }

    return disabled;
  });

  // ========================================================================
  // Slash command menu
  // ========================================================================
  const showSlashMenu = ref(false);
  const slashMenuPosition = ref({});
  const selectedSlashIndex = ref(0);
  const slashQuery = ref('');
  const slashStartPos = ref(null);

  const slashCommands = [
    { id: 'task', label: 'Task', icon: taskIcon },
    { id: 'appointment', label: 'Appointment', icon: appointmentIcon },
    { id: 'order', label: 'Order', icon: orderIcon },
    { id: 'document', label: 'Document', icon: documentIcon },
  ];

  const filteredSlashCommands = computed(() => {
    if (!slashQuery.value) return slashCommands;
    const q = slashQuery.value.toLowerCase();
    return slashCommands.filter(cmd => cmd.label.toLowerCase().startsWith(q));
  });

  const openSlashMenu = (pos) => {
    slashStartPos.value = pos;
    slashQuery.value = '';
    selectedSlashIndex.value = 0;
    slashMenuPosition.value = coordsToPosition(getCaretCoordinates(editor.value));
    showSlashMenu.value = true;
  };

  const closeSlashMenu = () => {
    showSlashMenu.value = false;
    slashQuery.value = '';
    slashStartPos.value = null;
    selectedSlashIndex.value = 0;
    scheduleSave();
  };

  const executeSlashCommand = (command) => {
    const instance = editor.value;
    if (!instance || slashStartPos.value === null) return;

    const currentPos = instance.state.selection.from;
    instance.chain()
      .focus()
      .deleteRange({ from: slashStartPos.value, to: currentPos })
      .run();

    emit('slashCommand', {
      type: command.id,
      insertContent: (content) => insertSlashCommandResult(content),
    });

    closeSlashMenu();
  };

  const handleSlashKeydown = (event) => {
    return handleMenuKeydown(event, filteredSlashCommands.value, selectedSlashIndex, executeSlashCommand, closeSlashMenu);
  };

  // ========================================================================
  // Patient variable menu ($ trigger) — same pattern as slash
  // ========================================================================
  const showVariableMenu = ref(false);
  const variableMenuPosition = ref({});
  const selectedVariableIndex = ref(0);
  const variableQuery = ref('');
  const variableStartPos = ref(null);

  const filteredVariables = computed(() => {
    const vars = (props.patientVariables || []).map(v => ({
      id: v.name,
      label: v.display_label || v.name,
      currentValue: v.current_value,
      matchType: 'variable',
    }));
    if (!variableQuery.value) return vars;
    const q = variableQuery.value.toLowerCase();
    return vars.filter(v => v.label.toLowerCase().includes(q));
  });

  const openVariableMenu = (pos) => {
    variableStartPos.value = pos;
    variableQuery.value = '';
    selectedVariableIndex.value = 0;
    variableMenuPosition.value = coordsToPosition(getCaretCoordinates(editor.value));
    showVariableMenu.value = true;
  };

  const closeVariableMenu = () => {
    showVariableMenu.value = false;
    variableQuery.value = '';
    variableStartPos.value = null;
    selectedVariableIndex.value = 0;
    scheduleSave();
  };

  const executeVariableCommand = (variable) => {
    if (variable.currentValue === null || variable.currentValue === undefined) return;
    const instance = editor.value;
    if (!instance || variableStartPos.value === null) return;

    const currentPos = instance.state.selection.from;
    instance.chain()
      .focus()
      .deleteRange({ from: variableStartPos.value, to: currentPos })
      .insertContent(variable.currentValue || '')
      .run();

    closeVariableMenu();
  };

  const handleVariableKeydown = (event) => {
    return handleMenuKeydown(event, filteredVariables.value, selectedVariableIndex, executeVariableCommand, closeVariableMenu);
  };

  // ========================================================================
  // Auto-match menu (3-letter debounced) — similar pattern, different trigger
  // ========================================================================
  const showAutoMatchMenu = ref(false);
  const autoMatchMenuPosition = ref({});
  const selectedAutoMatchIndex = ref(0);
  const autoMatchItems = ref([]);
  const autoMatchWordStart = ref(null);
  const autoMatchWordEnd = ref(null);

  // Build the combined list of matchable items from all sources
  const matchableItems = computed(() => {
    const items = [];

    // 1. Patient variables (from patientMatchData, excluding orderable_items by label)
    const orderableLabelSet = new Set(
      (props.patientMatchData?.orderable_items || []).map(i => (i.display_label || i.name || '').toLowerCase())
    );
    const matchVars = (props.patientMatchData?.patient_variables || [])
      .filter(v => !orderableLabelSet.has((v.display_label || v.name || '').toLowerCase()));

    const existingKeys = new Set(matchVars.map(v => (v.name || '').toLowerCase()));
    const extraVars = (props.patientVariables || [])
      .filter(v => !existingKeys.has((v.name || '').toLowerCase()));

    for (const v of [...matchVars, ...extraVars]) {
      items.push({
        matchType: 'variable',
        name: v.name,
        label: v.display_label || v.name,
        currentValue: v.current_value,
      });
    }

    // 2. Documents
    for (const doc of (props.patientMatchData?.documents || [])) {
      items.push({
        matchType: 'document',
        name: doc.key || doc.id,
        label: doc.fileName || doc.key,
        documentId: doc.id,
        fileName: doc.fileName,
        fileSize: doc.fileSize,
        contentType: doc.contentType,
        createdAt: doc.createdAt,
        docType: doc.type,
      });
    }

    // 3. Orderable items (restricted tests are marked as disabled)
    const disabledIds = disabledTestIds.value;
    for (const o of (props.patientMatchData?.orderable_items || [])) {
      items.push({
        matchType: 'order',
        name: o.key,
        label: o.display_label || o.key,
        itemId: o.item_id,
        disabled: disabledIds.has(o.item_id),
      });
    }

    return items;
  });

  const closeAutoMatchMenu = () => {
    showAutoMatchMenu.value = false;
    autoMatchItems.value = [];
    autoMatchWordStart.value = null;
    autoMatchWordEnd.value = null;
    selectedAutoMatchIndex.value = 0;
  };

  /**
   * Get candidate phrases at cursor for auto-matching.
   * Tries multi-word phrases (longest first) so "last name" matches before "name".
   */
  const getPhraseAtCursor = () => {
    const instance = editor.value;
    if (!instance) return null;

    const { from } = instance.state.selection;
    const { doc } = instance.state;

    const $pos = doc.resolve(from);
    const parentStart = $pos.start();
    const textBefore = doc.textBetween(parentStart, from, '');

    const trimmed = textBefore.trimEnd();
    if (!trimmed) return null;

    const words = trimmed.split(/\s+/);
    const maxWords = Math.min(words.length, 5);
    const allItems = matchableItems.value;

    for (let n = maxWords; n >= 1; n--) {
      const phraseWords = words.slice(-n);
      const phrase = phraseWords.join(' ');
      if (phrase.length < 3) continue;

      const query = phrase.toLowerCase();
      const hasMatch = allItems.some(v =>
        (v.label || '').toLowerCase().includes(query) ||
        (v.matchType === 'variable' && (v.name || '').toLowerCase().includes(query))
      );
      if (hasMatch) {
        const phraseStartInTrimmed = trimmed.length - phrase.length;
        const actualSubstr = trimmed.slice(phraseStartInTrimmed).toLowerCase();
        if (actualSubstr === query) {
          const phraseFrom = parentStart + phraseStartInTrimmed;
          return { phrase, from: phraseFrom, to: from };
        }
      }
    }

    return null;
  };

  const checkAutoMatch = () => {
    if (!props.isDraft) return;
    if (showSlashMenu.value || showVariableMenu.value || showAutoMatchMenu.value) return;

    const phraseInfo = getPhraseAtCursor();
    if (!phraseInfo) return;

    const query = phraseInfo.phrase.toLowerCase();
    const matches = matchableItems.value
      .filter(v =>
        (v.label || '').toLowerCase().includes(query) ||
        (v.matchType === 'variable' && (v.name || '').toLowerCase().includes(query))
      )
      .map(v => ({
        id: v.name,
        label: v.label,
        currentValue: v.currentValue,
        matchType: v.matchType,
        documentId: v.documentId,
        fileName: v.fileName,
        fileSize: v.fileSize,
        contentType: v.contentType,
        createdAt: v.createdAt,
        docType: v.docType,
        itemId: v.itemId,
        disabled: v.disabled,
      }));

    if (matches.length === 0) return;

    autoMatchItems.value = matches;
    autoMatchWordStart.value = phraseInfo.from;
    autoMatchWordEnd.value = phraseInfo.to;
    selectedAutoMatchIndex.value = 0;
    autoMatchMenuPosition.value = coordsToPosition(getCaretCoordinates(editor.value));
    showAutoMatchMenu.value = true;
  };

  const debouncedAutoMatch = debounce(() => checkAutoMatch(), 2000);

  const executeAutoMatch = (item) => {
    const instance = editor.value;
    if (!instance || autoMatchWordStart.value === null || autoMatchWordEnd.value === null) return;

    if (item.matchType === 'document') {
      const docDescription = formatDocumentCardDescription(item);
      instance.chain()
        .focus()
        .deleteRange({ from: autoMatchWordStart.value, to: autoMatchWordEnd.value })
        .insertContent([
          {
            type: 'cardAction',
            attrs: {
              type: 'document',
              id: item.documentId,
              title: item.fileName || 'Untitled Document',
              description: docDescription,
              freshlyCreated: true,
            },
          },
          { type: 'paragraph' },
        ])
        .run();
      closeAutoMatchMenu();
      return;
    }

    if (item.matchType === 'order') {
      if (item.disabled) return;
      instance.chain()
        .focus()
        .deleteRange({ from: autoMatchWordStart.value, to: autoMatchWordEnd.value })
        .run();
      emit('slashCommand', {
        type: 'order',
        itemId: item.itemId,
        insertContent: (content) => insertSlashCommandResult(content),
      });
      closeAutoMatchMenu();
      return;
    }

    // Default: patient variable
    if (item.currentValue === null || item.currentValue === undefined) return;

    instance.chain()
      .focus()
      .deleteRange({ from: autoMatchWordStart.value, to: autoMatchWordEnd.value })
      .insertContent(item.currentValue || '')
      .run();

    closeAutoMatchMenu();
  };

  const handleAutoMatchKeydown = (event) => {
    return handleMenuKeydown(event, autoMatchItems.value, selectedAutoMatchIndex, executeAutoMatch, closeAutoMatchMenu, { dismissOnOtherKey: true });
  };

  // ========================================================================
  // Unified helpers for editor hooks
  // ========================================================================

  /** Whether any menu is currently open */
  const isAnyMenuOpen = () => showSlashMenu.value || showVariableMenu.value || showAutoMatchMenu.value;

  /** Close all menus */
  const closeAllMenus = () => {
    if (showSlashMenu.value) closeSlashMenu();
    if (showVariableMenu.value) closeVariableMenu();
    if (showAutoMatchMenu.value) closeAutoMatchMenu();
  };

  /** Called from editor onUpdate to update query strings and trigger auto-match */
  const onEditorUpdate = (editorInstance) => {
    if (showAutoMatchMenu.value) closeAutoMatchMenu();
    debouncedAutoMatch();

    // Update slash query
    if (showSlashMenu.value && slashStartPos.value !== null) {
      const currentPos = editorInstance.state.selection.from;
      const text = editorInstance.state.doc.textBetween(slashStartPos.value, currentPos, '');
      if (text.startsWith('/')) {
        slashQuery.value = text.slice(1);
        selectedSlashIndex.value = 0;
      } else {
        closeSlashMenu();
      }
    }

    // Update variable query
    if (showVariableMenu.value && variableStartPos.value !== null) {
      const currentPos = editorInstance.state.selection.from;
      const text = editorInstance.state.doc.textBetween(variableStartPos.value, currentPos, '');
      if (text.startsWith('$')) {
        variableQuery.value = text.slice(1);
        selectedVariableIndex.value = 0;
      } else {
        closeVariableMenu();
      }
    }
  };

  /** Called from editor onSelectionUpdate to close menus when cursor moves away */
  const onSelectionUpdate = (editorInstance) => {
    if (showSlashMenu.value && slashStartPos.value !== null) {
      if (editorInstance.state.selection.from < slashStartPos.value) closeSlashMenu();
    }
    if (showVariableMenu.value && variableStartPos.value !== null) {
      if (editorInstance.state.selection.from < variableStartPos.value) closeVariableMenu();
    }
  };

  // ========================================================================
  // ProseMirror extension for key detection
  // ========================================================================
  const MenuExtension = Extension.create({
    name: 'editorMenus',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('editorMenus'),
          props: {
            handleKeyDown(view, event) {
              // Handle navigation in open menus
              if (showSlashMenu.value && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
                return handleSlashKeydown(event);
              }
              if (showVariableMenu.value && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
                return handleVariableKeydown(event);
              }
              if (showAutoMatchMenu.value) {
                if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
                  return handleAutoMatchKeydown(event);
                }
                closeAutoMatchMenu();
              }

              // Detect "/" for slash commands
              if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                const { from } = view.state.selection;
                nextTick(() => openSlashMenu(from));
              }

              // Detect "$" for patient variables
              if (event.key === '$' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                const { from } = view.state.selection;
                nextTick(() => openVariableMenu(from));
              }

              return false;
            },
          },
        }),
      ];
    },
  });

  return {
    // Slash menu
    showSlashMenu,
    slashMenuPosition,
    selectedSlashIndex,
    filteredSlashCommands,
    executeSlashCommand,

    // Variable menu
    showVariableMenu,
    variableMenuPosition,
    selectedVariableIndex,
    filteredVariables,
    executeVariableCommand,

    // Auto-match menu
    showAutoMatchMenu,
    autoMatchMenuPosition,
    selectedAutoMatchIndex,
    autoMatchItems,
    executeAutoMatch,

    // Shared
    isAnyMenuOpen,
    closeAllMenus,
    onEditorUpdate,
    onSelectionUpdate,
    debouncedAutoMatch,
    MenuExtension,
  };
}
